import { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Code2, Eye, Lock, Monitor, Rocket, Smartphone } from "lucide-react";
import { DemoInspector } from "./DemoInspector";
import { Reveal, SplitLines, EASE } from "./motion";

const INITIAL = {
  badge: { text: "OFERTA DE LANÇAMENTO", size: 12, color: "#0A7CFF", align: "left", tag: "span" },
  headline: { text: "Sua próxima oferta no ar em 3 minutos.", size: 44, color: "#0F172A", align: "left", tag: "h1" },
  sub: { text: "Clique em qualquer texto desta página e edite agora. É exatamente assim no Lowpages.", size: 17, color: "#475569", align: "left", tag: "p" },
  cta: { text: "Quero minha página", size: 15, color: "#ffffff", bg: "#F97316", align: "left", tag: "a" },
};
const LABELS = { badge: "Selo", headline: "Título h1", sub: "Parágrafo", cta: "Botão CTA" };

const Handles = () => (
  <>
    {["-left-1.5 -top-1.5", "-right-1.5 -top-1.5", "-left-1.5 -bottom-1.5", "-right-1.5 -bottom-1.5"].map((p) => (
      <span key={p} className={`absolute ${p} h-3 w-3 rounded-sm bg-white border-2 border-[var(--blue)] pointer-events-none`} />
    ))}
  </>
);

const Editable = ({ id, el, selected, onSelect, onText }) => {
  const html = useMemo(() => ({ __html: el.text }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const isCta = id === "cta";
  const style = { fontSize: el.size, color: el.color, textAlign: el.align, background: isCta ? el.bg : undefined };
  return (
    <div className={`relative ${el.align === "center" ? "text-center" : el.align === "right" ? "text-right" : ""}`}>
      <div
        data-testid={`demo-element-${id}`}
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        onFocus={() => onSelect(id)}
        onClick={() => onSelect(id)}
        onInput={(e) => onText(id, e.currentTarget.innerText)}
        style={style}
        className={`outline-none cursor-text transition-shadow duration-200 ${isCta ? "inline-block rounded-full px-6 py-3.5 font-semibold" : ""} ${id === "headline" ? "font-display font-bold tracking-tight leading-[1.05]" : ""} ${id === "badge" ? "font-mono tracking-[0.2em] font-semibold" : ""} ${selected ? "ring-2 ring-[var(--blue)] ring-offset-4 ring-offset-white rounded-sm" : "hover:ring-2 hover:ring-[var(--blue)]/40 hover:ring-offset-4 hover:ring-offset-white rounded-sm"}`}
        dangerouslySetInnerHTML={html}
      />
      {selected && (
        <>
          <span className="absolute -bottom-8 right-0 bg-[var(--blue)] text-white font-mono text-[10px] px-2 py-1 rounded pointer-events-none whitespace-nowrap z-10">{el.tag} · {el.size}px</span>
          <span className="absolute -inset-1 pointer-events-none"><Handles /></span>
        </>
      )}
    </div>
  );
};

const toHTML = (els) => `<section style="padding:64px;font-family:Instrument Sans">
  <span style="font-size:${els.badge.size}px;color:${els.badge.color}">${els.badge.text}</span>
  <h1 style="font-size:${els.headline.size}px;color:${els.headline.color};text-align:${els.headline.align}">${els.headline.text}</h1>
  <p style="font-size:${els.sub.size}px;color:${els.sub.color}">${els.sub.text}</p>
  <a href="#" style="background:${els.cta.bg};color:${els.cta.color};font-size:${els.cta.size}px;border-radius:999px;padding:14px 24px">${els.cta.text}</a>
</section>`;

export const LiveDemo = () => {
  const [els, setEls] = useState(INITIAL);
  const [selected, setSelected] = useState("headline");
  const [view, setView] = useState("preview");
  const [device, setDevice] = useState("desktop");
  const [pub, setPub] = useState("idle");
  const [saved, setSaved] = useState(true);
  const timer = useRef();

  const update = (id, patch) => {
    setEls((s) => ({ ...s, [id]: { ...s[id], ...patch } }));
    setSaved(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setSaved(true), 900);
  };
  const publish = () => {
    if (pub !== "idle") return;
    setPub("loading");
    setTimeout(() => setPub("done"), 1400);
  };

  return (
    <section id="demo" data-testid="demo-section" className="relative py-28 sm:py-36 px-6">
      <div className="absolute inset-0 dot-grid opacity-50 [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />
      <div className="relative mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-7">
            <Reveal y={16} className="eyebrow mb-8">Experimente agora</Reveal>
            <SplitLines as="h2" inView className="font-display font-bold tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl lg:text-6xl text-[var(--ink)]" lines={["Clique num texto.", <span className="text-slate-400">Edite. Publique.</span>]} />
          </div>
          <Reveal delay={0.2} className="lg:col-span-5 text-slate-600 text-base sm:text-lg leading-relaxed">
            Este é um pedaço do editor de verdade. Clique em qualquer texto abaixo, digite, mude cor e tamanho no painel — ou peça pro Low fazer por você.
          </Reveal>
        </div>

        <Reveal delay={0.1} className="card rounded-[1.75rem] overflow-hidden shadow-2xl">
          <div className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-3 border-b border-slate-100 bg-white">
            <div className="flex items-center gap-2 mr-2">
              <span className="h-3 w-3 rounded-full bg-slate-200" /><span className="h-3 w-3 rounded-full bg-slate-200" /><span className="h-3 w-3 rounded-full bg-slate-200" />
            </div>
            <span className="font-semibold text-sm text-[var(--ink)] hidden sm:inline">minha-oferta</span>
            <span data-testid="demo-saved-indicator" className={`font-mono text-[11px] transition-colors ${saved ? "text-emerald-600" : "text-slate-400"}`}>{saved ? "· salvo" : "· salvando…"}</span>
            <div className="ml-auto flex items-center gap-2">
              <div className="hidden sm:flex rounded-lg bg-slate-100 p-1">
                <button data-testid="demo-device-desktop" onClick={() => setDevice("desktop")} className={`h-8 w-9 rounded-md grid place-items-center ${device === "desktop" ? "bg-white shadow-sm text-[var(--blue)]" : "text-slate-500"}`}><Monitor size={15} /></button>
                <button data-testid="demo-device-mobile" onClick={() => setDevice("mobile")} className={`h-8 w-9 rounded-md grid place-items-center ${device === "mobile" ? "bg-white shadow-sm text-[var(--blue)]" : "text-slate-500"}`}><Smartphone size={15} /></button>
              </div>
              <div className="flex rounded-lg bg-slate-100 p-1 text-xs font-medium">
                <button data-testid="demo-view-preview" onClick={() => setView("preview")} className={`h-8 px-3 rounded-md flex items-center gap-1.5 ${view === "preview" ? "bg-white shadow-sm text-[var(--ink)]" : "text-slate-500"}`}><Eye size={14} /> Preview</button>
                <button data-testid="demo-view-code" onClick={() => setView("code")} className={`h-8 px-3 rounded-md flex items-center gap-1.5 ${view === "code" ? "bg-white shadow-sm text-[var(--ink)]" : "text-slate-500"}`}><Code2 size={14} /> Código</button>
              </div>
              <button data-testid="demo-publish-button" onClick={publish} disabled={pub !== "idle"} className={`btn !py-2.5 !px-4 text-xs ${pub === "done" ? "bg-emerald-500 text-white" : "btn-primary"}`}>
                {pub === "idle" && <><Rocket size={14} /> Publicar</>}
                {pub === "loading" && <><span className="h-3 w-3 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Publicando…</>}
                {pub === "done" && <><Check size={14} /> Publicado</>}
              </button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row bg-slate-50">
            <div className="flex-1 p-4 sm:p-8 min-h-[440px] flex items-start justify-center overflow-hidden">
              {view === "preview" ? (
                <motion.div layout transition={{ duration: 0.5, ease: EASE }} style={{ width: device === "mobile" ? 375 : "100%" }} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-12 flex flex-col gap-6 max-w-full">
                  <Editable id="badge" el={els.badge} selected={selected === "badge"} onSelect={setSelected} onText={(id, t) => update(id, { text: t })} />
                  <Editable id="headline" el={els.headline} selected={selected === "headline"} onSelect={setSelected} onText={(id, t) => update(id, { text: t })} />
                  <Editable id="sub" el={els.sub} selected={selected === "sub"} onSelect={setSelected} onText={(id, t) => update(id, { text: t })} />
                  <Editable id="cta" el={els.cta} selected={selected === "cta"} onSelect={setSelected} onText={(id, t) => update(id, { text: t })} />
                </motion.div>
              ) : (
                <pre data-testid="demo-code-view" className="w-full rounded-2xl bg-[var(--ink)] text-slate-200 p-6 font-mono text-xs leading-6 overflow-auto whitespace-pre-wrap">{toHTML(els)}</pre>
              )}
            </div>
            <DemoInspector el={els[selected]} label={LABELS[selected]} onChange={(next) => update(selected, next)} />
          </div>

          <AnimatePresence>
            {pub === "done" && (
              <motion.div data-testid="demo-published-url" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: EASE }} className="border-t border-emerald-100 bg-emerald-50 overflow-hidden">
                <div className="px-5 py-4 flex flex-wrap items-center gap-4 text-sm">
                  <span className="flex items-center gap-2 font-semibold text-emerald-700"><Check size={16} /> No ar em 1,4s ⚡</span>
                  <span className="flex items-center gap-2 rounded-full bg-white border border-emerald-200 px-4 py-2 font-mono text-xs text-slate-700"><Lock size={12} className="text-emerald-600" /> https://lowpages.site/p/minha-oferta</span>
                  <span className="text-slate-500 text-xs">SSL ativo · CDN global · visitas ilimitadas</span>
                  <a href="/register" data-testid="demo-published-cta" className="ml-auto btn btn-ink !py-2.5 !px-4 text-xs">Criar minha página de verdade</a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Reveal>
      </div>
    </section>
  );
};
